// DTOs matching backend SystemConfiguration API

export interface UserDto {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  username: string;
  emailVerified: boolean;
  isActive: boolean;
  totpEnabled: boolean;
  createdAt: Date;
  lastLoginAt?: Date;
  groups: UserGroupDto[];
  activeSessions: UserSessionDto[];
  companyPosition: string;
  profileFile?: {
    id: string;
    contentType: string;
    fileData: string; // the Base64 payload
  } | null;
}

export interface UserGroupDto {
  id: string;
  name: string;
  path: string;
}

export interface UserSessionDto {
  id: string;
  ipAddress: string;
  start: Date;
  lastAccess: Date;
  username: string;
  userId: string;
  clientId: string;
}

export interface CreateUserDto {
  firstName: string;
  lastName: string;
  email: string;
  groups: string[]; // Group IDs
}

export interface ChangeUserGroupsRequestDto {
  groups: string[];
  reason: string;
}

export interface GetUsersInput {
  filter?: string;
  isActive?: boolean;
  skipCount: number;
  maxResultCount: number;
  sorting?: string;
}

// Transform UserDto to Account interface for UI compatibility
export function userDtoToAccount(user: UserDto | null | undefined): any {
  if (!user) {
    const now = new Date();
    return {
      id: '',
      firstName: '',
      lastName: '',
      position: '',
      role: 'User',
      email: '',
      status: 'Inactive',
      initials: '',
      createdAt: now,
      updatedAt: now,
      permissions: [],
      emailVerified: false,
      totpEnabled: false,
      username: '',
      groups: [],
      activeSessions: [],
    };
  }

  const {
    id = '',
    firstName = '',
    lastName = '',
    email = '',
    username = '',
    groups = [],
    companyPosition = '',
    isActive,
    createdAt,
    lastLoginAt,
    emailVerified,
    totpEnabled,
    activeSessions = [],
    profileFile,
  } = user;

  const createdDate = createdAt ? new Date(createdAt) : new Date();
  const updatedDate = lastLoginAt ? new Date(lastLoginAt) : createdDate;

  return {
    id,
    firstName,
    lastName,
    position: companyPosition,
    role: groups[0]?.name ?? 'User',
    email,
    status: isActive ? 'Active' : 'Inactive',
    initials: `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase(),
    createdAt: createdDate,
    updatedAt: updatedDate,
    permissions: groups.map((g) => g.name),
    emailVerified: Boolean(emailVerified),
    totpEnabled: Boolean(totpEnabled),
    username,
    groups,
    activeSessions,
    avatarUrl:
      profileFile?.contentType && profileFile?.fileData
        ? `data:${profileFile.contentType};base64,${profileFile.fileData}`
        : undefined,
  };
}

// Transform Account to backend DTOs
export function accountToCreateUserDto(account: any): CreateUserDto {
  const nameParts = account.name.split(' ');
  const firstName = nameParts[0] || '';
  const lastName = nameParts.slice(1).join(' ') || '';

  return {
    firstName,
    lastName,
    email: account.email,
    groups: account.role || [],
  };
}
